/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { COMMON_FOCUS_ON_OPTIONS } from 'modules/dashboard/components/tab-container/trend-tabs/constants';
import { EXECUTION_TYPE } from 'modules/common/ui-controller/utils/constants';
/**
 * Function to get relevant values from application list when provide command values
 * @param {*} array
 * @param {*} list
 * @returns
 */
export const getValuesFromList = (array, list) => {
  const optionsSelected = [];
  array?.forEach((option) => {
    const optionSelected = list?.filter(
      (item) =>
        item?.label?.trim()?.replaceAll(' ', '').toLowerCase() ===
        option?.trim()?.replaceAll(' ', '').toLowerCase()
    );
    if (optionSelected?.length > 0) {
      optionsSelected.push(optionSelected[0]);
    }
  });
  //
  return optionsSelected;
};
/**
 * Function to get relevant values from application list when provide command values with total option
 * @param {*} array
 * @param {*} list
 * @returns
 */
export const getTrendValuesFromList = (array, list) => {
  const optionsSelected = [];
  array?.forEach((option) => {
    const optionSelected = list?.filter((item) => {
      if (item?.label === COMMON_FOCUS_ON_OPTIONS.OVERALL.label) {
        return (
          'Total'?.trim()?.replaceAll(' ', '').toLowerCase() ===
          option?.trim()?.replaceAll(' ', '').toLowerCase()
        );
      }
      return (
        item?.label?.trim()?.replaceAll(' ', '').toLowerCase() ===
        option?.trim()?.replaceAll(' ', '').toLowerCase()
      );
    });
    if (optionSelected?.length > 0) {
      optionsSelected.push(optionSelected[0]);
    }
  });
  //
  return optionsSelected;
};
/**
 * Function to get matched values from application list when provide command values
 * @param {*} array
 * @param {*} list
 * @returns
 */
export const getMatchedValuesFromList = (array, list) => {
  const optionsSelected = [];
  array?.forEach((option) => {
    const optionSelected = list?.filter((item) =>
      item
        ?.trim()
        ?.replaceAll(' ', '')
        .toLowerCase()
        .includes(option?.trim()?.replaceAll(' ', '').toLowerCase())
    );
    if (optionSelected?.length > 0) {
      optionsSelected.push(optionSelected[0]);
    }
  });
  //
  return optionsSelected;
};
/**
 * Function to get relevant hotel  values from application list when provide command values
 * @param {*} array
 * @param {*} list
 * @returns
 */
export const getHotelValuesFromList = (array, list) => {
  const optionsSelected = [];
  array?.forEach((option) => {
    const optionSelected = list?.filter(
      (item) =>
        item?.hotelCode === option ||
        item?.label
          ?.trim()
          ?.replaceAll(' ', '')
          .toLowerCase()
          .includes(option?.trim()?.replaceAll('%20', ' ')?.replaceAll(' ', '').toLowerCase()) ||
        item?.id === option  ||
        (item?.isPredefined && 
        item?.hotelCode === option ||
          item?.id?.trim()?.replaceAll(' ', '').toLowerCase() ===
            option?.trim()?.replaceAll('%20', ' ')?.replaceAll(' ', '').toLowerCase()) ||
        item?.altName
          ?.trim()
          ?.replaceAll(' ', '')
          .toLowerCase()
          .includes(option?.trim()?.replaceAll(' ', '').toLowerCase() )
    );
    if (optionSelected?.length > 0) {
      optionsSelected.push(optionSelected[0]);
    }
  });
  //
  return optionsSelected;
};
/**
 * Function to transform string into array
 * @param {*} value
 * @returns
 */
export const transformIntoArray = (value) => {
  let arr = [];
  if (value?.includes(',')) {
    arr = value?.split(',');
  } else {
    arr.push(value);
  }
  return arr;
};

/**
 * Sorts an array of objects based on a specified field while ensuring uniqueness.
 * @param {Array<Object>} dataArray - The array of objects to be sorted.
 * @param {string} sortField - The field based on which the array should be sorted.
 * @returns {Array<Object>} - A new array of sorted and unique objects.
 */
export const sortAndDistinctByField = (dataArray, sortField) =>
  Array.from(new Set(dataArray?.map((obj) => JSON.stringify(obj))))
    ?.map(JSON.parse)
    ?.sort((a, b) => a?.[sortField]?.localeCompare(b?.[sortField]));

/**
 * Function to normalize the strings (remove spaces and convert to lowercase)
 * @param {*} str
 * @returns
 */
export const normalizeString = (str) => str.replace(/\s+/g, '').toLowerCase();
/**
 * Check if all elements in array1 are included in array2 after normalization
 * @param {*} arr
 * @param {*} target
 * @returns
 */
export const isIncluded = (arr = [], target = []) =>
  target?.every((v) =>
    arr
      ?.map((a) => (a.label === COMMON_FOCUS_ON_OPTIONS.OVERALL.label ? 'Total' : a.label))
      ?.some((obj) => normalizeString(obj) === normalizeString(v))
  );
/**
 * Expand the ALL focus on command value into all applicable focus on options
 * (every option in focus on list except select all and overall), keeping the other
 * command values (ex: Total) and removing duplicates
 * @param {Array} values - focus on command values
 * @param {Array} list - focus on list
 * @returns
 */
export const expandFocusOnAll = (values, list) => {
  const allValue = normalizeString(EXECUTION_TYPE.ALL);
  if (
    !Array.isArray(values) ||
    !values.some((v) => typeof v === 'string' && normalizeString(v) === allValue)
  ) {
    return values;
  }
  const excludedLabels = [
    COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label,
    COMMON_FOCUS_ON_OPTIONS.OVERALL.label,
  ].map(normalizeString);
  const allOptions = (list ?? [])
    .map((item) => item?.label)
    .filter(
      (label) => typeof label === 'string' && !excludedLabels.includes(normalizeString(label))
    );
  const otherValues = values.filter(
    (v) => typeof v === 'string' && normalizeString(v) !== allValue
  );
  // remove duplicates
  const seen = new Set();
  return [...allOptions, ...otherValues].filter((value) => {
    const key = normalizeString(value);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
